import { Megaphone } from 'lucide-react';
import type { VerticalContent } from '../VerticalLandingPage';

export const marketingContent: VerticalContent = {
  vertical: 'marketing',
  industryLabel: 'Marketing & Agency',
  heroIcon: Megaphone,
  heroBadge: 'For agencies and in-house marketing teams',
  heroHeadline: 'Ship campaigns on time, protect margins, and stop losing weekends to status updates.',
  heroSub:
    'Karyva runs your client work the way agency veterans wish they\'d built it: retainer utilization in real time, scope creep visible the moment it happens, and client-facing dashboards that make status calls 15 minutes instead of an hour.',
  trustStats: [
    { value: 'Retainer', label: 'utilization, live' },
    { value: 'Client', label: 'portal per account' },
    { value: '14-day', label: 'free trial, no card needed' },
  ],
  pains: [
    {
      title: 'Retainer hours bleed and nobody notices until month-end',
      body:
        'The team is "feeling busy" for two weeks. By the time the time-tracker spits out the report, you\'re 130% over and the partner has to decide whether to eat it or have an awkward client conversation.',
    },
    {
      title: 'Scope creep is invisible until the client wonders why the invoice grew',
      body:
        'A "quick tweak" becomes three rounds of revisions. Nobody flagged it as out-of-scope because it didn\'t feel big enough at the time. The PM is now an accountant arguing about hours from six weeks ago.',
    },
    {
      title: 'Friday status updates eat half the team\'s afternoon',
      body:
        'Every account lead spends 90 minutes copying Jira tickets, Slack threads, and Google Doc revisions into a client status doc. The client skims it. The team resents it.',
    },
    {
      title: 'Approvals come back as Slack messages, voice memos, and tracked-changes Word docs',
      body:
        'A creative gets feedback in three places and merges the wrong version. Two rounds later, the client says "I thought we agreed on the other one." Nobody can prove who said what.',
    },
  ],
  bridges: [
    {
      painHook: 'Retainer utilization invisible until too late',
      feature: 'Live retainer dashboard with burn alerts',
      body:
        'Hours roll up to the account in real time. Set a threshold — 60%, 80%, 100% — and the account lead and partner get notified before the budget runs out. Margin protected proactively, not retroactively.',
    },
    {
      painHook: 'Scope creep that only surfaces at invoice time',
      feature: 'In-scope vs out-of-scope tagging on every request',
      body:
        'Tag a new request as in-scope or out-of-scope when it lands. Out-of-scope items roll up into a change-order proposal the account lead can send in two clicks. The client says yes or no — nobody re-litigates billing six weeks later.',
    },
    {
      painHook: 'Friday status doc that nobody enjoys',
      feature: 'Client portal with live status — no copy-paste',
      body:
        'Every account has a client-facing portal that mirrors the team\'s workspace, filtered to what the client should see. Status updates compose themselves; the account lead reviews and publishes. Friday afternoons return to humans.',
    },
    {
      painHook: 'Approvals scattered across Slack, email, and Word',
      feature: 'Single approval workflow with version pinning',
      body:
        'Every creative round has one approval thread. The client approves with a click or leaves comments inline. The approved version is pinned, dated, and signed. No more "I thought we agreed on V3."',
    },
  ],
  workflowTitle: 'A campaign sprint, in Karyva',
  workflowSteps: [
    {
      when: 'Kickoff',
      what:
        'Account lead spins up a campaign workspace from a template — discovery, creative, production, launch, recap. Client gets portal access scoped to status, deliverables, and approvals — nothing internal.',
    },
    {
      when: 'Creative round',
      what:
        'Designer uploads V1. Client opens the portal, leaves inline comments, approves V2 with a click. Karyva pins the approved version and timestamps the sign-off — no "which file was final?" the night before launch.',
    },
    {
      when: 'Mid-campaign check',
      what:
        'Retainer at 70% with two weeks left. Partner gets an alert in Slack and a one-click view of where the hours went. Conversation happens before the burn, not after.',
    },
    {
      when: 'Wrap',
      what:
        'Campaign recap auto-generates: deliverables shipped, hours by phase, what shifted scope, what we\'d do differently. The account lead edits the recap, sends it to the client, archives the workspace.',
    },
  ],
  whyKaryva: [
    'Built for retainer + project hybrid work — not pure waterfall, not pure agile.',
    'Client portal per account with role-scoped views. Clients see status; they don\'t see your hourly rates.',
    'Scope and budget alerts that fire before you lose margin, not after.',
    'Templates for campaign types — brand launch, paid media sprint, content series — not a blank Kanban board.',
  ],
  testimonial: {
    quote:
      'We stopped doing the Friday status doc entirely. Clients see more than they used to, and our team got their Fridays back.',
    name: 'Managing Director (pilot customer)',
    role: 'Brand agency — 12 active retainers',
  },
  logos: ['Northbeam', 'Studio Volt', 'Resonant', 'Field Notes'],
  faqs: [
    {
      q: 'Does this replace our time-tracking tool (Harvest, Toggl, Clockify)?',
      a: 'Karyva has built-in time tracking that rolls up to retainer utilization. If your team already loves their tracker, we integrate — pull hours in, push project context out. Either way you get a live retainer view without double-entry.',
    },
    {
      q: 'Can clients access Karyva without a paid seat?',
      a: 'Yes — every account includes a client portal. Clients view status, approve creative, and leave comments without taking a paid seat. You pay only for internal users.',
    },
    {
      q: 'How does it handle retainer + project work for the same client?',
      a: 'A client account can hold a retainer workspace AND project-based workspaces side by side. Retainer hours track against the monthly bucket; project hours track against the project budget. The account-level dashboard shows both.',
    },
    {
      q: 'Is this overkill for a small studio (3-10 people)?',
      a: 'No — the Starter tier ($29/mo) is built for studios under 10 seats. You get retainer tracking, client portals, and templates. Most studios graduate from Asana/ClickUp to Karyva precisely because those tools don\'t understand retainer work.',
    },
  ],
};
